import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Car, Search, ShieldCheck, XCircle, Clock } from 'lucide-react';
import Drawer from '../../components/common/Drawer/Drawer';
import api from '../../api/axios';
import styles from './Vehicles.module.css';

const Vehicles = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchVehicles();
  }, []);

  const fetchVehicles = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/security/vehicles');
      setVehicles(data);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  const [formData, setFormData] = useState({ vehicleNumber: '', ownerDetails: '', type: 'Visitor' });

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post('/security/vehicles', formData);
      setVehicles([data, ...vehicles]);
      setIsDrawerOpen(false);
      setFormData({ vehicleNumber: '', ownerDetails: '', type: 'Visitor' });
      toast.success('Vehicle logged successfully!');
    } catch (err) {
      toast.error('Failed to log vehicle');
    }
  };

  const handleExit = async (id) => {
    try {
      const { data } = await api.put(`/security/vehicles/${id}/exit`);
      setVehicles(vehicles.map(v => v._id === id ? data : v));
      toast.success('Vehicle exit marked');
    } catch (err) {
      toast.error('Failed to mark exit');
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.pageHeader}>
        <div>
          <h1>Vehicle Logs</h1>
          <p>Monitor resident vehicles and register guest parking</p>
        </div>
        <button className={styles.addBtn} onClick={() => setIsDrawerOpen(true)}>
          <Car size={16} /> Guest Vehicle Entry
        </button>
      </div>

      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <div className={styles.searchBox}>
            <Search size={16} className={styles.searchIcon} />
            <input type="text" placeholder="Search vehicle number..." />
          </div>
        </div>

        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Vehicle No.</th>
                <th>Type</th>
                <th>Flat No.</th>
                <th>In Time</th>
                <th>Out Time</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan="7">Loading...</td></tr> : vehicles.map(v => {
                const isResident = v.type === 'Resident';
                const status = isResident ? 'Authorized' : (v.exitTime ? 'Exited' : 'Inside');
                return (
                <tr key={v._id}>
                  <td><strong>{v.vehicleNumber}</strong></td>
                  <td>
                    <span className={isResident ? styles.badgeRes : styles.badgeGuest}>
                      {v.type}
                    </span>
                  </td>
                  <td><strong>{v.ownerDetails || '-'}</strong></td>
                  <td>{v.entryTime ? new Date(v.entryTime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : (v.createdAt ? new Date(v.createdAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : '-')}</td>
                  <td>{v.exitTime ? new Date(v.exitTime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : '-'}</td>
                  <td>
                    <span className={`${styles.badgeStatus} ${styles[status.toLowerCase()]}`}>
                      {status === 'Authorized' && <ShieldCheck size={12} />}
                      {status}
                    </span>
                  </td>
                  <td>
                    {status === 'Inside' && (
                      <button className={styles.btnAction} onClick={() => handleExit(v._id)}>
                        Mark Exit
                      </button>
                    )}
                    {status === 'Authorized' && <span className={styles.textMuted}>Resident</span>}
                    {status === 'Exited' && <span className={styles.textMuted}>Done</span>}
                  </td>
                </tr>
              )})}
            </tbody>
          </table>
        </div>

        {/* Mobile View */}
        <div className={styles.mobileList}>
          {loading ? <p style={{padding: '16px'}}>Loading...</p> : vehicles.map(v => {
            const isResident = v.type === 'Resident';
            const status = isResident ? 'Authorized' : (v.exitTime ? 'Exited' : 'Inside');
            return (
              <div key={v._id} className={styles.mobileCard}>
                <div className={styles.mobileCardHeader}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <strong style={{ fontSize: '14px', color: '#111827', textTransform: 'uppercase' }}>{v.vehicleNumber}</strong>
                    <span className={isResident ? styles.badgeRes : styles.badgeGuest} style={{ width: 'fit-content' }}>
                      {v.type}
                    </span>
                  </div>
                  <span className={`${styles.badgeStatus} ${styles[status.toLowerCase()]}`}>
                    {status === 'Authorized' && <ShieldCheck size={12} />}
                    {status}
                  </span>
                </div>
                <div className={styles.mobileCardMeta}>
                  <div className={styles.metaRow}>
                    <span>Flat / Owner: <strong>{v.ownerDetails || '-'}</strong></span>
                  </div>
                  <div className={styles.metaRow}>
                    <span>In: {v.entryTime ? new Date(v.entryTime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : (v.createdAt ? new Date(v.createdAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : '-')}</span>
                    <span>Out: {v.exitTime ? new Date(v.exitTime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : '-'}</span>
                  </div>
                  <div className={styles.metaRow} style={{ marginTop: '4px' }}>
                    {status === 'Inside' && (
                      <button className={styles.btnAction} style={{ width: '100%', justifyContent: 'center' }} onClick={() => handleExit(v._id)}>
                        Mark Exit
                      </button>
                    )}
                    {status === 'Authorized' && <span className={styles.textMuted} style={{ width: '100%', textAlign: 'center', padding: '6px 0' }}>Resident</span>}
                    {status === 'Exited' && <span className={styles.textMuted} style={{ width: '100%', textAlign: 'center', padding: '6px 0' }}>Done</span>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <Drawer isOpen={isDrawerOpen} onClose={() => setIsDrawerOpen(false)} title="Guest Vehicle Entry">
        <form className="drawerForm" onSubmit={handleSubmit}>
          <div className="formGroup">
            <label>Vehicle Number</label>
            <input name="vehicleNumber" value={formData.vehicleNumber} onChange={handleChange} required placeholder="e.g. MH 04 AB 1234" style={{ textTransform: 'uppercase' }} />
          </div>
          <div className="formGroup">
            <label>Visiting Flat / Owner Name</label>
            <input name="ownerDetails" value={formData.ownerDetails} onChange={handleChange} required placeholder="e.g. A-101 / John" />
          </div>
          
          <div className="formActions">
            <button type="button" className="btnCancel" onClick={() => setIsDrawerOpen(false)}>Cancel</button>
            <button type="submit" className="btnSubmit">Log Vehicle Entry</button>
          </div>
        </form>
      </Drawer>
    </div>
  );
};

export default Vehicles;
